import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import moment, { Moment } from 'moment';
import { Translate } from 'react-localize-redux';
import { CallHistoryMethodAction, push } from 'connected-react-router';

import AppState from '@webapp/store/state';
import { Order } from '../../../types/orders/order';
import {
  CancelSubscription,
  CreateSubscription,
} from '../../../store/subscription/action-creators';
import SimpleArtist from '../../../../../../common/types/artist/simple-artist';

import { getWidth } from '@components/styles';
import { isEntryLevelPlan } from '../../../utils/subsciptions/is-entry-level-plan';

import Flex from '@components/layout/flex';
import Typography from '@components/basics/typography';
import MotionOpacity from '@components/motion/motion-opacity';
import TextareaWithCount from '@components/form/textareawithcount';
import UnselectedIcon from '@components/basics/icons/unselected';
import DashboardContainer from '@components/layout/dashboard-container';
import CompletedIcon from '@components/basics/icons/achievements-icons/completed';
import { Subscription } from '../../../../../../services/subscription/types/subscription';
import { CustomerMetadata } from '../../../../../../services/payment/types/api/customer-metadata';
import Button from '@components/actions/button';
import LoadingCtrl from '@webapp/controllers/loading';

interface DowngradeConfirmationPageProps {
  className?: string;
  artist?: SimpleArtist;
  currentSubscription?: Subscription;
  currentOrder?: Order;
  currentPlan: string;
  features: string[];
  selectedPlan: string;
  expiresAt?: Moment;
  isMobile: boolean;
  goToSubscriptions: () => CallHistoryMethodAction;
  createSubscription: (
    currentSubscription: undefined | Subscription,
    order: Order,
    customer: CustomerMetadata,
    redirectTo: string
  ) => CreateSubscription;
  cancelSubscription: () => CancelSubscription;
}

const mapStateToProps = (state: AppState, props: any) => {
  const { clientId, id } = props.match?.params;

  const foundSubscription = state.subscriptionManager.summary?.artists
    .find((s) => s.id === clientId)
    ?.subscriptions.find((s) => s.id === id);

  const currentOrder = state.orders.basket.find(
    (p) => p.product.category === 'PLAN'
  );

  return {
    currentSubscription: foundSubscription?.subscription,
    artist: state.artist.myArtists.find((a) => a.id === clientId),
    currentPlan: foundSubscription?.plan || '',
    features:
      state.products.list.find(
        (p) => p.category === 'PLAN' && p.name === foundSubscription?.plan
      )?.features || [],
    expiresAt: foundSubscription?.nextBilling
      ? moment(foundSubscription?.nextBilling)
      : undefined,
    currentOrder,
    selectedPlan: currentOrder?.product.name || '',
    isMobile: state.ui.mobile,
  };
};

const mapDispatchToProps = (dispatch: Dispatch, props: any) => {
  const { clientId } = props.match?.params;

  return {
    cancelSubscription: () => dispatch(new CancelSubscription(clientId)),
    goToSubscriptions: () => dispatch(push(`/artist/subscriptions`)),
    createSubscription: (
      currentSubscription: undefined | Subscription,
      order: Order,
      customer: CustomerMetadata,
      redirectTo: string
    ) =>
      dispatch(
        new CreateSubscription(
          undefined,
          undefined,
          clientId,
          order,
          customer,
          'artist',
          currentSubscription,
          undefined,
          undefined,
          undefined,
          undefined,
          'plan downgrade',
          redirectTo
        )
      ),
  };
};

const DowngradeConfirmationPage = ({
  ...props
}: DowngradeConfirmationPageProps) => {
  const [selectedItem, setSelectedItem] = React.useState<string>('');
  const [otherText, setOtherText] = React.useState<string>('');
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const reasons = [
    {
      label: `ARTIST_PROFILE.SUBSCRIPTION.UNSUBSCRIBE.POLL.ITEM.1`,
      value: 'item-1',
    },
    {
      label: 'ARTIST_PROFILE.SUBSCRIPTION.UNSUBSCRIBE.POLL.ITEM.2',
      value: 'item-2',
    },
    {
      label: `ARTIST_PROFILE.SUBSCRIPTION.UNSUBSCRIBE.POLL.ITEM.3`,
      value: 'item-3',
    },
    {
      label: 'ARTIST_PROFILE.SUBSCRIPTION.UNSUBSCRIBE.POLL.ITEM.4',
      value: 'item-4',
    },
    {
      label: 'ARTIST_PROFILE.SUBSCRIPTION.UNSUBSCRIBE.POLL.ITEM.5',
      value: 'item-5',
    },
    {
      label: 'ARTIST_PROFILE.SUBSCRIPTION.UNSUBSCRIBE.POLL.ITEM.6',
      value: 'item-6',
    },
  ];

  const handleDowngrade = () => {
    if (!(props.currentOrder && props.artist)) {
      return;
    }

    if (isEntryLevelPlan(props.currentOrder.product)) {
      setIsLoading(true);

      if (props.currentSubscription) {
        return props.cancelSubscription();
      }

      return props.createSubscription(
        undefined,
        props.currentOrder,
        props.artist,
        '/artist/subscriptions'
      );
    }
  };

  return (
    <DashboardContainer className={props.className}>
      <Wrapper animate>
        <Flex flexDirection="column" gap={10} className="mb-30">
          <Typography type="subheader" customFont="semi-bold">
            <Translate id="ARTIST_PROFILE.SUBSCRIPTION.UNSUBSCRIBE.TITLE" />
          </Typography>
          <Typography className="text" type="title" weight="400">
            <Translate id="ARTIST_PROFILE.SUBSCRIPTION.UNSUBSCRIBE.TEXT" />
          </Typography>
        </Flex>
        <PollWrapper className="mb-40">
          <Typography type="section-title" customFont="medium">
            <Translate id="ARTIST_PROFILE.SUBSCRIPTION.UNSUBSCRIBE.POLL.TITLE" />
          </Typography>
          {reasons.map((item) => (
            <Flex
              className={`poll-row ${
                selectedItem && selectedItem !== item.value
                  ? 'has-selected'
                  : ''
              }`}
              key={item.value}
              gap={10}
              alignItems="center"
              justifyContent="space-between"
              onClick={() => setSelectedItem(item.value)}
            >
              <Typography weight={selectedItem === item.value ? '700' : '400'}>
                <Translate id={item.label} data={{ plan: props.currentPlan }} />
              </Typography>
              {selectedItem === item.value ? (
                <CompletedIcon className="icon" />
              ) : (
                <UnselectedIcon className="icon" />
              )}
            </Flex>
          ))}
          <Flex
            className={`poll-row ${
              selectedItem && selectedItem !== 'other' ? 'has-selected' : ''
            }`}
            gap={10}
            alignItems="center"
            justifyContent="space-between"
            onClick={() => setSelectedItem('other')}
          >
            <Typography weight={selectedItem === 'other' ? '700' : '400'}>
              <Translate id="COMMON.POLL.OTHER" />
            </Typography>
            {selectedItem === 'other' ? (
              <CompletedIcon className="icon" />
            ) : (
              <UnselectedIcon className="icon" />
            )}
          </Flex>
          {selectedItem === 'other' && (
            <MotionOpacity>
              <TextareaWithCount
                maxLength={300}
                value={otherText}
                label={<Translate id="COMMON.POLL.OTHER.TEXT_AREA" />}
                onChange={(value) => setOtherText(value)}
              />
            </MotionOpacity>
          )}
        </PollWrapper>

        <ButtonGroup className="mt-40">
          <Button
            buttonType="primary"
            onClick={() => props.goToSubscriptions()}
            disabled={isLoading}
          >
            <Translate id="ONBOARDING.DOWNGRADE.BTN.CANCEL" />
          </Button>
          <Button
            buttonType="secondary"
            onClick={handleDowngrade}
            className={selectedItem ? 'btn-downgrade' : ''}
            disabled={!!selectedItem || isLoading}
            hasLoader={isLoading}
          >
            {isLoading ? (
              <LoadingCtrl
                className={`loading-animation ${isLoading ? 'is-loading' : ''}`}
                loading
                isSVG
              />
            ) : (
              <Translate id="ONBOARDING.DOWNGRADE.BTN.DOWNGRADE" />
            )}
          </Button>
        </ButtonGroup>
      </Wrapper>
    </DashboardContainer>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(DowngradeConfirmationPage)``);

const Wrapper = styled(motion.div)`
  margin: auto;
  max-width: ${getWidth(6)};

  @media ${({ theme }) => theme.device.tablet} {
    max-width: ${getWidth(10)};
  }

  .text {
    color: ${({ theme }) => theme.colors.tertiary};
  }
`;

const PollWrapper = styled.div`
  padding: 15px;
  border-radius: 10px;
  background-color: ${({ theme }) => theme.colors.box};
  display: grid;
  grid-gap: 10px;

  @media ${({ theme }) => theme.device.tablet} {
    padding: 30px;
    grid-gap: 20px;
  }

  .poll-row {
    cursor: pointer;
    &.has-selected {
      opacity: 0.5;
    }
  }

  .icon {
    width: 24px;
    height: 24px;
  }
`;

const ButtonGroup = styled(Flex)`
  flex-direction: column;
  @media ${({ theme }) => theme.device.tablet} {
    flex-direction: row;
  }
  button {
    margin-bottom: 5px;
    &:last-child {
      margin-bottom: 0;
    }
    @media ${({ theme }) => theme.device.tablet} {
      width: 50%;
      margin-right: 5px;
      margin-bottom: 0;

      &:last-child {
        margin-right: 0;
      }
    }
  }

  .btn-downgrade span {
    color: ${({ theme }) => theme.colors.accent};
  }
`;
